import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, File, Download, Trash2, Loader2, CheckCircle2, AlertCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { assetFileService, AssetFile } from '@/firebase/assetFileService';
import { assetFactsService } from '@/firebase/assetFactsService';
import { extractFieldValuesFromDocument } from '@/services/geminiService';
import { getRequiredFieldsForAssetFacts } from '@/services/reprocessAssetFactsService';
import { getIndustryType } from '@/firebase/organizationService';
import { autoCompleteChecklistsForAsset } from '@/services/autoCompleteChecklistService';
import { autoCompleteChecklistsFromFacts } from '@/services/autoCompleteChecklistFromFactsService';
import { reprocessAssetFacts, reprocessSingleDocument } from '@/services/reprocessAssetFactsService';
import { checklistEvidenceService, ChecklistEvidence } from '@/firebase/checklistEvidenceService';
import { checklistCompletionService } from '@/firebase/checklistCompletionService';
import { checklistService } from '@/firebase/checklistService';
import { assetService } from '@/firebase/assetService';
import { getFieldsForAssetType, AssetTypeEvidenceField } from '@/services/assetTypeFieldsService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';

interface AssetFileUploadProps {
  assetId: string;
  onFileUploaded?: (file: AssetFile) => void;
  onFileDeleted?: (fileId: string) => void;
}

const AssetFileUpload: React.FC<AssetFileUploadProps> = ({ 
  assetId, 
  onFileUploaded,
  onFileDeleted 
}) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  
  const [files, setFiles] = useState<AssetFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [encryptingFiles, setEncryptingFiles] = useState<Set<string>>(new Set());
  const [uploadStatus, setUploadStatus] = useState<Map<string, { status: 'encrypting' | 'uploading' | 'complete'; message?: string }>>(new Map());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [reprocessingFiles, setReprocessingFiles] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);
  const [processingFileId, setProcessingFileId] = useState<string | null>(null);
  const [fileEvidenceMap, setFileEvidenceMap] = useState<Map<string, Array<{
    checklistName: string;
    itemText: string;
    itemOrder?: number;
    extractedFields: Record<string, any>;
  }>>>(new Map());
  const [fileFactsMap, setFileFactsMap] = useState<Map<string, Array<{
    key: string;
    value: any;
    confidence?: 'high' | 'medium' | 'low';
  }>>>(new Map());
  const [assetTypeFields, setAssetTypeFields] = useState<AssetTypeEvidenceField[]>([]);
  const [allAssetFacts, setAllAssetFacts] = useState<Record<string, any>>({});
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [expandedFacts, setExpandedFacts] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing files
  React.useEffect(() => {
    loadFiles();
  }, [assetId, currentOrganization?.id]);

  const loadFiles = async () => {
    if (!assetId || !currentOrganization?.id) return;
    
    setIsLoading(true);
    try {
      const result = await assetFileService.getAssetFiles(assetId, currentOrganization.id);
      if (result.success && result.data) {
        // Ensure all files have a status (default to 'pending' if missing)
        const filesWithStatus = result.data.map(file => ({
          ...file,
          status: file.status || 'pending'
        }));
        console.log('[AssetFileUpload] Loaded files:', filesWithStatus.map(f => ({
          id: f.id,
          fileName: f.fileName,
          status: f.status,
          uploadedAt: f.uploadedAt
        })));
        setFiles(filesWithStatus);
        // Stop loading indicator - show files immediately
        setIsLoading(false);
        
        // Load additional data in the background (non-blocking)
        // Use Promise.all to load in parallel
        Promise.all([
          loadEvidenceForFiles(result.data),
          loadFactsForFiles(result.data),
          loadAssetTypeFieldsAndFacts()
        ]).catch(error => {
          console.error('Error loading additional file data:', error);
          // Don't show error notification - these are background loads
        });
      } else {
        console.error('Error loading files:', result.error);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      showNotification('Failed to load files', 'error');
      setIsLoading(false);
    }
  };

  const loadEvidenceForFiles = async (assetFiles: AssetFile[]) => {
    if (!currentOrganization?.id) return;

    const evidenceMap = new Map<string, Array<{
      checklistName: string;
      itemText: string;
      itemOrder?: number;
      extractedFields: Record<string, any>;
    }>>();

    // Load evidence for all files in parallel
    const evidencePromises = assetFiles
      .filter(file => file.id)
      .map(async (file) => {
        try {
          const evidenceResult = await checklistEvidenceService.getEvidenceByFileId(
            file.id!,
            currentOrganization.id!
          );

          if (evidenceResult.success && evidenceResult.data && evidenceResult.data.length > 0) {
            return { fileId: file.id!, evidence: evidenceResult.data };
          }
          return { fileId: file.id!, evidence: [] };
        } catch (error) {
          console.error(`Error loading evidence for file ${file.id}:`, error);
          return { fileId: file.id!, evidence: [] };
        }
      });

    const evidenceResults = await Promise.all(evidencePromises);

    // Collect unique completion IDs and checklist IDs to batch load
    const completionIds = new Set<string>();
    const checklistIds = new Set<string>();
    
    for (const { evidence } of evidenceResults) {
      for (const ev of evidence) {
        if (ev.checklistCompletionId) {
          completionIds.add(ev.checklistCompletionId);
        }
      }
    }

    // Load all completions in parallel
    const completionPromises = Array.from(completionIds).map(async (completionId) => {
      try {
        const completionResult = await checklistCompletionService.getCompletion(completionId);
        if (completionResult.success && completionResult.data) {
          if (completionResult.data.checklistId) {
            checklistIds.add(completionResult.data.checklistId);
          }
          return { completionId, completion: completionResult.data };
        }
        return null;
      } catch (error) {
        console.error(`Error loading completion ${completionId}:`, error);
        return null;
      }
    });

    const completions = (await Promise.all(completionPromises))
      .filter((c): c is { completionId: string; completion: any } => c !== null);
    const completionMap = new Map(completions.map(c => [c.completionId, c.completion]));

    // Load all checklists in parallel
    const checklistPromises = Array.from(checklistIds).map(async (checklistId) => {
      try {
        const checklistResult = await checklistService.getChecklist(checklistId);
        if (checklistResult.success && checklistResult.data) {
          return { checklistId, checklist: checklistResult.data };
        }
        return null;
      } catch (error) {
        console.error(`Error loading checklist ${checklistId}:`, error);
        return null;
      }
    });

    const checklists = (await Promise.all(checklistPromises))
      .filter((c): c is { checklistId: string; checklist: any } => c !== null);
    const checklistMap = new Map(checklists.map(c => [c.checklistId, c.checklist]));

    // Now process evidence for each file
    for (const { fileId, evidence } of evidenceResults) {
      if (evidence.length === 0) continue;

      const fileEvidence: Array<{
        checklistName: string;
        itemText: string;
        itemOrder?: number;
        extractedFields: Record<string, any>;
      }> = [];

      // Group evidence by checklist completion
      const fileCompletionMap = new Map<string, ChecklistEvidence[]>();
      for (const ev of evidence) {
        const completionId = ev.checklistCompletionId;
        if (!fileCompletionMap.has(completionId)) {
          fileCompletionMap.set(completionId, []);
        }
        fileCompletionMap.get(completionId)!.push(ev);
      }

      // Process each completion
      for (const [completionId, evidenceList] of fileCompletionMap.entries()) {
        const completion = completionMap.get(completionId);
        if (!completion) continue;

        const checklistId = completion.checklistId;
        let checklistName = completion.checklistName || 'Unknown Checklist';
        if (checklistId) {
          const checklist = checklistMap.get(checklistId);
          if (checklist) {
            checklistName = checklist.name;
          }
        }

        // Process each evidence item
        for (const evidence of evidenceList) {
          // Parse extracted fields from notes
          let extractedFields: Record<string, any> = {};
          if (evidence.notes) {
            const match = evidence.notes.match(/Auto-extracted fields: ({.*})/);
            if (match) {
              try {
                extractedFields = JSON.parse(match[1]);
              } catch (e) {
                console.warn('Failed to parse extracted fields:', e);
              }
            }
          }

          // Find item order from checklist
          let itemOrder: number | undefined;
          if (checklistId) {
            const checklist = checklistMap.get(checklistId);
            if (checklist) {
              const item = checklist.items.find(
                (item: any) => item.text === evidence.itemText
              );
              if (item) {
                itemOrder = item.order;
              }
            }
          }

          fileEvidence.push({
            checklistName,
            itemText: evidence.itemText,
            itemOrder,
            extractedFields,
          });
        }
      }

      if (fileEvidence.length > 0) {
        evidenceMap.set(fileId, fileEvidence);
      }
    }

    setFileEvidenceMap(evidenceMap);
  };

  const loadFactsForFiles = async (assetFiles: AssetFile[]) => {
    if (!assetId) return;

    try {
      // Get all asset facts
      const factsResult = await assetFactsService.getAssetFacts(assetId);
      if (!factsResult.success || !factsResult.data || !factsResult.data.facts) {
        return;
      }

      const allFacts = factsResult.data.facts;
      const factsMap = new Map<string, Array<{
        key: string;
        value: any;
        confidence?: 'high' | 'medium' | 'low';
      }>>();

      // Group facts by extractedFrom (file name)
      for (const file of assetFiles) {
        if (!file.fileName) continue;

        const fileFacts: Array<{
          key: string;
          value: any;
          confidence?: 'high' | 'medium' | 'low';
        }> = [];

        // Find facts extracted from this file
        // Check both legacy extractedFrom and new extractedValues array
        Object.entries(allFacts).forEach(([factKey, fact]) => {
          if (fact.source === 'EXTRACTED') {
            // Check legacy extractedFrom (for backward compatibility)
            if (fact.extractedFrom === file.fileName) {
              fileFacts.push({
                key: factKey,
                value: fact.value,
                confidence: fact.confidence,
              });
            }
            // Check new extractedValues array
            else if (fact.extractedValues && fact.extractedValues.length > 0) {
              const extractedValue = fact.extractedValues.find(
                ev => ev.fileName === file.fileName || ev.fileId === file.id
              );
              if (extractedValue) {
                fileFacts.push({
                  key: factKey,
                  value: extractedValue.value,
                  confidence: extractedValue.confidence,
                });
              }
            }
          }
        });

        // Always set the entry (even if empty) so we can show "no facts" message for files that have been processed
        if (file.id) {
          factsMap.set(file.id, fileFacts);
        }
      }

      setFileFactsMap(factsMap);
    } catch (error) {
      console.error('Error loading facts for files:', error);
    }
  };

  const loadAssetTypeFieldsAndFacts = async () => {
    if (!assetId || !currentOrganization?.id) return;

    try {
      // 1. Get asset to find assetTypeId
      const assetResult = await assetService.getAsset(assetId);
      if (!assetResult.success || !assetResult.data || !assetResult.data.assetTypeId) {
        return;
      }

      const assetTypeId = assetResult.data.assetTypeId;

      // 2. Get asset type fields
      const fieldsResult = await getFieldsForAssetType(assetTypeId);
      if (fieldsResult.success && fieldsResult.fields) {
        setAssetTypeFields(fieldsResult.fields);
      }

      // 3. Get all asset facts
      const factsResult = await assetFactsService.getAssetFacts(assetId);
      if (factsResult.success && factsResult.data && factsResult.data.facts) {
        setAllAssetFacts(factsResult.data.facts);
      }
    } catch (error) {
      console.error('Error loading asset type fields and facts:', error);
    }
  };

  const reloadEvidenceAndFactsForFile = async (fileId: string) => {
    if (!currentOrganization?.id) return;

    const file = files.find(f => f.id === fileId);
    if (!file) return;

    // Reload evidence for this specific file
    try {
      const evidenceResult = await checklistEvidenceService.getEvidenceByFileId(
        fileId,
        currentOrganization.id
      );

      if (evidenceResult.success && evidenceResult.data && evidenceResult.data.length > 0) {
        const fileEvidence: Array<{
          checklistName: string;
          itemText: string;
          itemOrder?: number;
          extractedFields: Record<string, any>;
        }> = [];

        // Group evidence by checklist completion
        const completionMap = new Map<string, ChecklistEvidence[]>();
        for (const evidence of evidenceResult.data) {
          const completionId = evidence.checklistCompletionId;
          if (!completionMap.has(completionId)) {
            completionMap.set(completionId, []);
          }
          completionMap.get(completionId)!.push(evidence);
        }

        // Load checklist completion and checklist details for each evidence
        for (const [completionId, evidenceList] of completionMap.entries()) {
          try {
            const completionResult = await checklistCompletionService.getCompletion(completionId);
            if (!completionResult.success || !completionResult.data) continue;

            const completion = completionResult.data;
            const checklistId = completion.checklistId;

            // Get checklist details
            let checklistName = completion.checklistName || 'Unknown Checklist';
            if (checklistId) {
              const checklistResult = await checklistService.getChecklist(checklistId);
              if (checklistResult.success && checklistResult.data) {
                checklistName = checklistResult.data.name;
              }
            }

            // Process each evidence item
            for (const evidence of evidenceList) {
              // Parse extracted fields from notes
              let extractedFields: Record<string, any> = {};
              if (evidence.notes) {
                const match = evidence.notes.match(/Auto-extracted fields: ({.*})/);
                if (match) {
                  try {
                    extractedFields = JSON.parse(match[1]);
                  } catch (e) {
                    console.warn('Failed to parse extracted fields:', e);
                  }
                }
              }

              // Find item order from checklist
              let itemOrder: number | undefined;
              if (checklistId) {
                const checklistResult = await checklistService.getChecklist(checklistId);
                if (checklistResult.success && checklistResult.data) {
                  const item = checklistResult.data.items.find(
                    item => item.text === evidence.itemText
                  );
                  if (item) {
                    itemOrder = item.order;
                  }
                }
              }

              fileEvidence.push({
                checklistName,
                itemText: evidence.itemText,
                itemOrder,
                extractedFields,
              });
            }
          } catch (error) {
            console.error(`Error loading completion ${completionId}:`, error);
          }
        }

        if (fileEvidence.length > 0) {
          setFileEvidenceMap(prev => {
            const next = new Map(prev);
            next.set(fileId, fileEvidence);
            return next;
          });
        }
      } else {
        // Clear evidence if none found
        setFileEvidenceMap(prev => {
          const next = new Map(prev);
          next.delete(fileId);
          return next;
        });
      }
    } catch (error) {
      console.error(`Error loading evidence for file ${fileId}:`, error);
    }

    // Reload all asset facts (for the comprehensive view)
    await loadAssetTypeFieldsAndFacts();
  };

  const handleFileSelect = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (!currentOrganization?.id || !user) {
      showNotification('Organization or user not found', 'error');
      return;
    }

    const filesArray = Array.from(selectedFiles);
    const uploadedFiles: File[] = [];
    const fileIdMap = new Map<string, string>(); // File.name -> AssetFile.id
    
    // Upload all files first
    for (const file of filesArray) {
      const fileId = `${Date.now()}_${Math.random().toString(36).substring(7)}_${file.name}`;
      setUploadingFiles(prev => new Set(prev).add(fileId));
      setUploadStatus(prev => new Map(prev).set(fileId, { status: 'uploading', message: 'Preparing upload...' }));

      try {
        const result = await assetFileService.uploadAssetFile(
          assetId,
          file,
          {
            organizationId: currentOrganization.id,
            uploadedBy: {
              id: user.id,
              name: user.name || 'Unknown',
              email: user.email || 'Unknown',
            },
            onProgress: (status, message) => {
              setUploadStatus(prev => {
                const newMap = new Map(prev);
                newMap.set(fileId, { status, message });
                return newMap;
              });
              
              // Track encryption state separately for UI
              if (status === 'encrypting') {
                setEncryptingFiles(prev => new Set(prev).add(fileId));
              } else if (status === 'uploading') {
                setEncryptingFiles(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(fileId);
                  return newSet;
                });
              } else if (status === 'complete') {
                setEncryptingFiles(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(fileId);
                  return newSet;
                });
              }
            },
          }
        );

        if (result.success && result.data) {
          setUploadStatus(prev => {
            const newMap = new Map(prev);
            newMap.set(fileId, { status: 'complete', message: 'Upload successful' });
            return newMap;
          });
          
          setFiles(prev => [result.data!, ...prev]);
          showNotification(`File "${file.name}" uploaded successfully`, 'success');
          onFileUploaded?.(result.data);
          uploadedFiles.push(file); // Keep reference for auto-completion
          if (result.data.id) {
            fileIdMap.set(file.name, result.data.id); // Map file name to AssetFile ID
          }
          
          // Clear status after 3 seconds
          setTimeout(() => {
            setUploadStatus(prev => {
              const newMap = new Map(prev);
              newMap.delete(fileId);
              return newMap;
            });
            setUploadingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(fileId);
              return newSet;
            });
          }, 3000);
          
          // Reload evidence for the newly uploaded file
          if (result.data.id) {
            await loadEvidenceForFiles([result.data]);
          }

          // Extract field values from the uploaded file using checklist field definitions
          // This works for all organization types, extracting fields defined in checklists
          if (currentOrganization?.id) {
            try {
              // Get field definitions from checklists for this asset
              const fieldsResult = await getRequiredFieldsForAssetFacts(assetId, currentOrganization.id);
              
              if (fieldsResult.success && fieldsResult.requiredFields && fieldsResult.requiredFields.length > 0) {
                // Extract field values using the checklist-defined fields
                const fieldResult = await extractFieldValuesFromDocument(file, fieldsResult.requiredFields);
                
                if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
                  // Prepare facts for saving
                  const factsToSave: Record<string, { value: any; source: 'EXTRACTED'; extractedFrom?: string; confidence?: 'high' | 'medium' | 'low' }> = {};
                  
                  Object.entries(fieldResult.fieldValues).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                      factsToSave[key] = {
                        value,
                        source: 'EXTRACTED',
                        extractedFrom: file.name,
                        confidence: fieldResult.confidence,
                      };
                    }
                  });

                  if (Object.keys(factsToSave).length > 0) {
                    await assetFactsService.setFacts(assetId, factsToSave, currentOrganization.id);
                    
                    // Reload facts for files to update the fileFactsMap so the UI can display them
                    // Get updated files list to pass to loadFactsForFiles
                    const updatedFilesResult = await assetFileService.getAssetFiles(assetId, currentOrganization.id);
                    if (updatedFilesResult.success && updatedFilesResult.data) {
                      await loadFactsForFiles(updatedFilesResult.data);
                    }
                    
                    const factsCount = Object.keys(factsToSave).length;
                    showNotification(
                      `AI extracted ${factsCount} field value${factsCount !== 1 ? 's' : ''} from ${file.name}`,
                      'success'
                    );
                  }
                }
              } else {
                // No field definitions found - this is OK, just means no checklists are configured yet
                console.log('No field definitions found for this asset. Checklists may not be configured with evidence fields yet.');
              }
            } catch (factsError: any) {
              console.error('Error extracting field values:', factsError);
              // Don't show error notification for extraction failures, just log
            }
          }
        } else {
          setUploadStatus(prev => {
            const newMap = new Map(prev);
            newMap.delete(fileId);
            return newMap;
          });
          showNotification(`Failed to upload "${file.name}": ${result.error}`, 'error');
        }
      } catch (error: any) {
        console.error('Error uploading file:', error);
        
        // Clear encryption status if encryption failed
        setEncryptingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
        
        // Clear upload status
        setUploadStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete(fileId);
          return newMap;
        });
        
        // Show error notification with specific message for encryption failures
        const errorMessage = error?.message || 'Unknown error';
        if (errorMessage.includes('Encryption failed') || errorMessage.includes('GLBA compliance')) {
          showNotification(`Encryption failed for "${file.name}". Upload cancelled to maintain security compliance.`, 'error');
        } else {
          showNotification(`Error uploading "${file.name}": ${errorMessage}`, 'error');
        }
      } finally {
        setUploadingFiles(prev => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    }

    // Note: Auto-completion is now triggered manually via "Auto-Run Checklists" button
    // Files are processed to extract facts only, not to complete checklists
  }, [assetId, currentOrganization?.id, user, showNotification, onFileUploaded]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDownload = async (file: AssetFile) => {
    if (!file.id || !currentOrganization?.id) {
      showNotification('File information missing', 'error');
      return;
    }

    setDownloadingFiles(prev => new Set(prev).add(file.id!));

    try {
      const result = await assetFileService.downloadAssetFile(file.id, currentOrganization.id);
      
      if (result.success && result.data) {
        // Create download link and trigger download
        const url = URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showNotification(`File "${file.fileName}" downloaded successfully`, 'success');
      } else {
        showNotification(`Failed to download file: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error: any) {
      console.error('Error downloading file:', error);
      showNotification(`Failed to download file: ${error.message}`, 'error');
    } finally {
      setDownloadingFiles(prev => {
        const next = new Set(prev);
        next.delete(file.id!);
        return next;
      });
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    if (!currentOrganization?.id) return;

    setDeletingFiles(prev => new Set(prev).add(fileId));

    try {
      const result = await assetFileService.deleteAssetFile(fileId, currentOrganization.id);
      if (result.success) {
        setFiles(prev => prev.filter(f => f.id !== fileId));
        showNotification('File deleted successfully', 'success');
        onFileDeleted?.(fileId);
      } else {
        showNotification(`Failed to delete file: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting file:', error);
      showNotification(`Failed to delete file: ${error.message}`, 'error');
    } finally {
      setDeletingFiles(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleReprocessDocument = async (fileId: string) => {
    if (!currentOrganization?.id || !user?.id) {
      showNotification('Organization or user not found', 'error');
      return;
    }

    setReprocessingFiles(prev => new Set(prev).add(fileId));

    try {
      const reprocessResult = await reprocessSingleDocument(
        assetId,
        fileId,
        currentOrganization.id
      );

      if (reprocessResult.success) {
        if (reprocessResult.factsExtracted > 0) {
          showNotification(
            `Document reprocessed: Extracted ${reprocessResult.factsExtracted} fact${reprocessResult.factsExtracted !== 1 ? 's' : ''}`,
            'success'
          );
        } else {
          showNotification(
            'Document processed, but no required fields were found to extract from this document.',
            'info'
          );
        }

        // Reload evidence and facts information for this specific file only
        await reloadEvidenceAndFactsForFile(fileId);
      } else {
        showNotification(
          `Reprocessing failed: ${reprocessResult.errors?.join(', ') || 'Unknown error'}`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('Error during document reprocessing:', error);
      showNotification(`Reprocessing failed: ${error.message}`, 'error');
    } finally {
      setReprocessingFiles(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return '📊';
    return '📎';
  };

  const handleAutoComplete = async () => {
    if (!currentOrganization?.id || !user?.id) {
      showNotification('Organization or user not found', 'error');
      return;
    }

    setIsAutoCompleting(true);
    try {
      // Use the new function that works with existing facts (no file reprocessing)
      const autoCompleteResult = await autoCompleteChecklistsFromFacts(
        assetId,
        currentOrganization.id,
        user.id
      );

      if (autoCompleteResult.success) {
        const summary = [
          `Processed ${autoCompleteResult.checklistsProcessed} checklist${autoCompleteResult.checklistsProcessed !== 1 ? 's' : ''}`,
          autoCompleteResult.itemsCompleted > 0 && `${autoCompleteResult.itemsCompleted} item${autoCompleteResult.itemsCompleted !== 1 ? 's' : ''} completed`,
          autoCompleteResult.itemsPending > 0 && `${autoCompleteResult.itemsPending} item${autoCompleteResult.itemsPending !== 1 ? 's' : ''} pending review`,
          autoCompleteResult.checklistsCompleted > 0 && `${autoCompleteResult.checklistsCompleted} checklist${autoCompleteResult.checklistsCompleted !== 1 ? 's' : ''} completed`,
          autoCompleteResult.checklistsPaused > 0 && `${autoCompleteResult.checklistsPaused} checklist${autoCompleteResult.checklistsPaused !== 1 ? 's' : ''} paused/pending`,
        ].filter(Boolean).join(', ');

        showNotification(
          `Auto-completion: ${summary}`,
          autoCompleteResult.checklistsCompleted > 0 ? 'success' : 'info'
        );

        if (autoCompleteResult.errors && autoCompleteResult.errors.length > 0) {
          console.warn('Auto-completion errors:', autoCompleteResult.errors);
          showNotification(
            `Auto-completion completed with errors: ${autoCompleteResult.errors?.join(', ') || 'Unknown error'}`,
            'error'
          );
        }
        
        // Trigger a custom event to notify parent components to refresh completion data
        window.dispatchEvent(new CustomEvent('checklistAutoCompleted', { 
          detail: { assetId } 
        }));
      } else {
        showNotification(
          `Auto-completion failed: ${autoCompleteResult.errors?.join(', ') || 'Unknown error'}`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('Error during auto-completion:', error);
      showNotification(`Auto-completion failed: ${error.message}`, 'error');
    } finally {
      setIsAutoCompleting(false);
    }
  };

  const handleReprocessFacts = async () => {
    if (!currentOrganization?.id || !user?.id) {
      showNotification('Organization or user not found', 'error');
      return;
    }

    if (files.length === 0) {
      showNotification('No files available to reprocess', 'error');
      return;
    }

    setIsAutoCompleting(true);
    try {
      // Reprocess asset facts based on checklist required fields
      const reprocessResult = await reprocessAssetFacts(
        assetId,
        currentOrganization.id
      );

      if (reprocessResult.success) {
        const summary = [
          reprocessResult.requiredFieldsCount && `${reprocessResult.requiredFieldsCount} required field${reprocessResult.requiredFieldsCount !== 1 ? 's' : ''} identified`,
          reprocessResult.checklistsCount && `from ${reprocessResult.checklistsCount} checklist${reprocessResult.checklistsCount !== 1 ? 's' : ''}`,
        ].filter(Boolean).join(' ');

        showNotification(
          `Required fields list built: ${summary}. Fields will be used when processing documents.`,
          'success'
        );

        // Reload evidence information after reprocessing
        await loadFiles();

        if (reprocessResult.errors && reprocessResult.errors.length > 0) {
          console.warn('Reprocessing errors:', reprocessResult.errors);
          showNotification(
            `Reprocessing completed with errors: ${reprocessResult.errors?.join(', ') || 'Unknown error'}`,
            'error'
          );
        }
      } else {
        showNotification(
          `Reprocessing failed: ${reprocessResult.errors?.join(', ') || 'Unknown error'}`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('Error during asset facts reprocessing:', error);
      showNotification(`Reprocessing failed: ${error.message}`, 'error');
    } finally {
      setIsAutoCompleting(false);
    }
  };

  const handleReprocessDocuments = async () => {
    if (!currentOrganization?.id || !user?.id) {
      showNotification('Organization or user not found', 'error');
      return;
    }

    if (files.length === 0) {
      showNotification('No files available to reprocess', 'error');
      return;
    }

    setIsAutoCompleting(true);
    let processedCount = 0;
    let errorCount = 0;

    try {
      // Process files one by one with UI feedback
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.id) continue;

        setProcessingFileId(file.id);
        
        try {
          // Reprocess single document
          const result = await reprocessSingleDocument(
            assetId,
            file.id,
            currentOrganization.id
          );

          if (result.success) {
            processedCount++;
            // Reload evidence and facts for this specific file
            await reloadEvidenceAndFactsForFile(file.id);
            
            if (result.factsExtracted > 0) {
              showNotification(
                `Processed "${file.fileName}": ${result.factsExtracted} fact${result.factsExtracted !== 1 ? 's' : ''} extracted`,
                'success'
              );
            }
          } else {
            errorCount++;
            console.error(`Error reprocessing ${file.fileName}:`, result.errors);
          }
        } catch (error: any) {
          errorCount++;
          console.error(`Error reprocessing ${file.fileName}:`, error);
        }

        // Small delay between files to avoid overwhelming the system
        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // After all files are processed, auto-complete checklists
      if (processedCount > 0) {
        showNotification('Processing complete. Auto-completing checklists...', 'info');
        
        const autoCompleteResult = await autoCompleteChecklistsFromFacts(
          assetId,
          currentOrganization.id,
          user.id
        );

        if (autoCompleteResult.success) {
          const summary = [
            `Processed ${processedCount} file${processedCount !== 1 ? 's' : ''}`,
            autoCompleteResult.itemsCompleted > 0 && `${autoCompleteResult.itemsCompleted} item${autoCompleteResult.itemsCompleted !== 1 ? 's' : ''} completed`,
            autoCompleteResult.itemsPending > 0 && `${autoCompleteResult.itemsPending} item${autoCompleteResult.itemsPending !== 1 ? 's' : ''} pending review`,
          ].filter(Boolean).join(', ');

          showNotification(
            `Reprocessing complete: ${summary}`,
            autoCompleteResult.itemsCompleted > 0 ? 'success' : 'info'
          );
        }
      }

      if (errorCount > 0) {
        showNotification(
          `Reprocessing completed with ${errorCount} error${errorCount !== 1 ? 's' : ''}`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('Error reprocessing documents:', error);
      showNotification(`Failed to reprocess documents: ${error.message}`, 'error');
    } finally {
      setIsAutoCompleting(false);
      setProcessingFileId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading files...</span>
      </div>
    );
  }

  if (isAutoCompleting) {
    return (
      <div className="flex items-center justify-center p-8 bg-blue-50 rounded-lg border border-blue-200">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-blue-700 font-medium">Analyzing documents and auto-completing checklists...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
        />
        
        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-600 mb-2">
          Drag and drop files here, or{' '}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-blue-600 hover:text-blue-700 underline"
          >
            browse
          </button>
        </p>
        <p className="text-xs text-gray-500">
          Supported: PDF, DOC, DOCX, XLS, XLSX, Images, TXT
        </p>
      </div>

      {/* Auto-Complete Buttons */}
      {files.length > 0 && currentOrganization && user && (
        <div className="flex justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleAutoComplete}
            disabled={isAutoCompleting}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            title="Auto-complete checklists using existing facts (fast, no document reprocessing)"
          >
            {isAutoCompleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Auto-Run Checklists</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleReprocessDocuments}
            disabled={isAutoCompleting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            title="Reprocess all uploaded documents to extract all required fields (slower, but more thorough)"
          >
            {isAutoCompleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Reprocessing...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Reprocess Documents</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleReprocessFacts}
            disabled={isAutoCompleting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            title="Reprocess asset facts based on checklist required fields (extracts only fields needed by checklists)"
          >
            {isAutoCompleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Reprocessing Facts...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Reprocess Asset Facts</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Asset Facts - All Fields from Asset Type */}
      {assetTypeFields.length > 0 && (
        <div className="mb-6 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Asset Facts ({assetTypeFields.length} fields)
          </h3>
          <div className="border rounded-lg divide-y bg-white">
            {assetTypeFields.map((field) => {
              const fact = allAssetFacts[field.key];
              const hasValues = fact && fact.extractedValues && fact.extractedValues.length > 0;
              const hasLegacyValue = fact && fact.extractedFrom && !hasValues;
              const isExpanded = expandedFacts.has(field.key);
              
              // Get primary value (highest confidence or first value)
              let primaryValue: any = null;
              let primarySource: string = '';
              let primaryConfidence: string = '';
              
              if (hasValues && fact.extractedValues.length > 0) {
                // Use highest confidence value as primary
                const sorted = [...fact.extractedValues].sort((a, b) => {
                  const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                  return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                });
                primaryValue = sorted[0].value;
                primarySource = sorted[0].fileName;
                primaryConfidence = sorted[0].confidence || '';
              } else if (hasLegacyValue) {
                primaryValue = fact.value;
                primarySource = fact.extractedFrom || '';
                primaryConfidence = fact.confidence || '';
              }
              
              // Map file ID to file name
              let displaySource = primarySource;
              if (primarySource && !primarySource.includes('.') && primarySource.length > 10) {
                const file = files.find(f => f.id === primarySource);
                if (file) {
                  displaySource = file.fileName;
                }
              }
              
              // Format value for display
              const formatValue = (val: any) => {
                if (val === null || val === undefined) return 'No value';
                if (field.type === 'boolean') return val ? 'Yes' : 'No';
                if (Array.isArray(val)) return val.map((item: any) => String(item ?? '').trim()).filter(Boolean).join(', ') || '—';
                if (field.type === 'date' && val) {
                  try {
                    const date = val instanceof Date ? val : new Date(val);
                    return date.toLocaleDateString();
                  } catch {
                    return String(val);
                  }
                }
                return String(val);
              };
              
              const hasValue = primaryValue !== null && primaryValue !== undefined;
              
              return (
                <div 
                  key={field.key} 
                  className={`p-3 transition-colors ${
                    hasValue 
                      ? 'hover:bg-gray-50 bg-white' 
                      : 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-400'
                  }`}
                >
                  <div 
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => {
                      setExpandedFacts(prev => {
                        const next = new Set(prev);
                        if (next.has(field.key)) {
                          next.delete(field.key);
                        } else {
                          next.add(field.key);
                        }
                        return next;
                      });
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-900">{field.label}</span>
                        {field.required && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">
                            Required
                          </span>
                        )}
                        <span className="text-xs text-gray-400 capitalize">{field.type}</span>
                      </div>
                      
                      {/* Minimal view - always shown */}
                      <div className="ml-6 mt-1">
                        {primaryValue !== null && primaryValue !== undefined ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-medium">
                              {formatValue(primaryValue)}
                            </span>
                            {displaySource && (
                              <span className="text-xs text-gray-500">
                                from {displaySource}
                              </span>
                            )}
                            {primaryConfidence && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                primaryConfidence === 'high' ? 'bg-green-100 text-green-800' :
                                primaryConfidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {primaryConfidence}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No value</span>
                        )}
                      </div>
                      
                      {/* Expanded view - shown when clicked */}
                      {isExpanded && (
                        <div className="ml-6 mt-3 space-y-2">
                          <div className="text-xs font-semibold text-gray-600 mb-2">All Extracted Values:</div>
                          
                          {hasValues ? (
                            fact.extractedValues.map((ev: any, idx: number) => {
                              // Map fileId to fileName
                              let displayFileName = ev.fileName;
                              if (ev.fileId) {
                                const file = files.find(f => f.id === ev.fileId);
                                if (file) {
                                  displayFileName = file.fileName;
                                }
                              } else if (ev.fileName && !ev.fileName.includes('.') && ev.fileName.length > 10) {
                                const file = files.find(f => f.id === ev.fileName);
                                if (file) {
                                  displayFileName = file.fileName;
                                }
                              }
                              
                              return (
                                <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="text-xs font-medium text-gray-700">
                                        {formatValue(ev.value)}
                                      </div>
                                      <div className="text-[10px] text-gray-600 mt-0.5">
                                        From: {displayFileName}
                                      </div>
                                    </div>
                                    {ev.confidence && (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        ev.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                        ev.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-orange-100 text-orange-800'
                                      }`}>
                                        {ev.confidence}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : hasLegacyValue ? (
                            <div className="bg-gray-50 p-2 rounded border border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-xs font-medium text-gray-700">
                                    {formatValue(fact.value)}
                                  </div>
                                  <div className="text-[10px] text-gray-600 mt-0.5">
                                    From: {displaySource}
                                  </div>
                                </div>
                                {fact.confidence && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    fact.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                    fact.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {fact.confidence}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">
                              No values extracted from any document
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Files List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Uploaded Files ({files.length})
          </h3>
          <div className="border rounded-lg divide-y">
            {files.map((file) => {
              const isUploading = uploadingFiles.has(`${Date.now()}_${file.fileName}`);
              const isDeleting = file.id && deletingFiles.has(file.id);
              const isDownloading = file.id && downloadingFiles.has(file.id);
              const isReprocessing = file.id && reprocessingFiles.has(file.id);
              const isCurrentlyProcessing = file.id === processingFileId;
              const evidenceInfo = file.id ? fileEvidenceMap.get(file.id) : undefined;
              const fileFacts = file.id ? fileFactsMap.get(file.id) : undefined;

              return (
                <div
                  key={file.id}
                  className="p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-2xl">{getFileIcon(file.fileType)}</span>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => {
                            if (file.id) {
                              setExpandedFiles(prev => {
                                const next = new Set(prev);
                                if (next.has(file.id!)) {
                                  next.delete(file.id!);
                                } else {
                                  next.add(file.id!);
                                }
                                return next;
                              });
                            }
                          }}
                          className="text-sm font-medium text-gray-900 truncate hover:text-blue-600 transition-colors text-left w-full"
                        >
                          {file.fileName}
                        </button>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{formatFileSize(file.fileSize)}</span>
                          <span>•</span>
                          <span className="capitalize">
                            {file.status === 'pending' && 'Pending'}
                            {file.status === 'processing' && 'Processing'}
                            {file.status === 'analyzed' && 'Analyzed'}
                            {file.status === 'error' && 'Error'}
                            {!file.status && 'Pending'}
                          </span>
                          {file.uploadedAt && (() => {
                            try {
                              // Handle Firestore Timestamp
                              let date: Date;
                              if (file.uploadedAt.toDate) {
                                date = file.uploadedAt.toDate();
                              } else if (file.uploadedAt.seconds) {
                                date = new Date(file.uploadedAt.seconds * 1000);
                              } else if (file.uploadedAt instanceof Date) {
                                date = file.uploadedAt;
                              } else if (typeof file.uploadedAt === 'string') {
                                date = new Date(file.uploadedAt);
                              } else {
                                return null;
                              }
                              return (
                                <>
                                  <span>•</span>
                                  <span>
                                    {date.toLocaleDateString()}
                                  </span>
                                </>
                              );
                            } catch (error) {
                              console.error('Error parsing uploadedAt date:', error, file.uploadedAt);
                              return null;
                            }
                          })()}
                          {isCurrentlyProcessing && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600 font-medium flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Processing...
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {file.status === 'error' && file.error && (
                        <div className="flex items-center space-x-1" title={file.error}>
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          <span className="text-xs text-red-600">{file.error}</span>
                        </div>
                      )}
                      {file.status === 'error' && !file.error && (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                      {file.status === 'analyzed' && (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      )}
                      <button
                        type="button"
                        onClick={() => file.id && handleReprocessDocument(file.id)}
                        disabled={isReprocessing || isUploading || file.status === 'error'}
                        className="p-1.5 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                        title="Reprocess this document to extract facts"
                      >
                        {isReprocessing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(file)}
                        disabled={isDownloading || isUploading}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                        title="Download"
                      >
                        {file.id && downloadingFiles.has(file.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => file.id && handleDelete(file.id)}
                        disabled={isDeleting || isUploading}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {isDeleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Asset Facts Extracted from this File - Only show when expanded */}
                  {file.id && expandedFiles.has(file.id) && fileFacts !== undefined && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      {fileFacts.length > 0 ? (
                        <>
                          <p className="text-xs font-medium text-gray-700 mb-2">Asset Facts Extracted:</p>
                          <div className="flex flex-wrap gap-2">
                            {fileFacts.map((fact, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200"
                                title={`Confidence: ${fact.confidence || 'unknown'}`}
                              >
                                <span className="font-medium text-green-900">{fact.key}:</span>
                                <span className="ml-1">
                                  {fact.value === null || fact.value === undefined
                                    ? 'N/A'
                                    : typeof fact.value === 'boolean'
                                    ? fact.value
                                      ? 'Yes'
                                      : 'No'
                                    : typeof fact.value === 'object'
                                    ? JSON.stringify(fact.value)
                                    : String(fact.value)}
                                </span>
                                {fact.confidence && (
                                  <span className={`ml-2 text-xs px-1 rounded ${
                                    fact.confidence === 'high' ? 'bg-green-200 text-green-900' :
                                    fact.confidence === 'medium' ? 'bg-yellow-200 text-yellow-900' :
                                    'bg-orange-200 text-orange-900'
                                  }`}>
                                    {fact.confidence}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-gray-500 italic">
                          No asset facts extracted from this document. No required fields were found to extract.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Evidence Information - Only show when expanded */}
                  {file.id && expandedFiles.has(file.id) && evidenceInfo && evidenceInfo.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">Used in Checklists:</p>
                      <div className="space-y-2">
                        {evidenceInfo.map((evidence, idx) => (
                          <div key={idx} className="bg-blue-50 rounded p-2 text-xs">
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-blue-900">
                                {evidence.checklistName}
                              </span>
                              {evidence.itemOrder !== undefined && (
                                <span className="text-blue-700">• Item #{evidence.itemOrder}</span>
                              )}
                            </div>
                            <p className="text-blue-800 mt-1">{evidence.itemText}</p>
                            {Object.keys(evidence.extractedFields).length > 0 && (
                              <div className="mt-2 pt-2 border-t border-blue-200">
                                <p className="font-medium text-blue-900 mb-1">Extracted Fields:</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(evidence.extractedFields).map(([key, value]) => (
                                    <span
                                      key={key}
                                      className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800"
                                    >
                                      <span className="font-medium">{key}:</span>
                                      <span className="ml-1">
                                        {value === null || value === undefined
                                          ? 'N/A'
                                          : typeof value === 'boolean'
                                          ? value
                                            ? 'Yes'
                                            : 'No'
                                          : String(value)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Uploading indicator */}
      {/* Upload Status Display */}
      {uploadingFiles.size > 0 && (
        <div className="space-y-2 mt-4">
          {Array.from(uploadingFiles).map((fileId) => {
            const status = uploadStatus.get(fileId);
            const isEncrypting = encryptingFiles.has(fileId);
            
            return (
              <div key={fileId} className="flex items-center space-x-2 text-sm p-2 bg-blue-50 rounded border border-blue-200">
                {isEncrypting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-blue-700 font-medium">
                      {status?.message || 'Encrypting file...'}
                    </span>
                  </>
                ) : status?.status === 'uploading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-blue-700 font-medium">
                      {status?.message || 'Uploading file...'}
                    </span>
                  </>
                ) : status?.status === 'complete' ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 font-medium">
                      {status?.message || 'Upload successful'}
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-blue-700">Processing...</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssetFileUpload;

